'use client';

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getDocuments, uploadDocument, downloadDocument } from '@/lib/services/portalService';
import { createClient } from '@/lib/supabase/client';

interface Document {
  id: string;
  file_name: string;
  file_type: string;
  file_size: string;
  uploader_type: 'admin' | 'client';
  created_at: string;
  category: string;
  storage_path: string;
}

interface DocumentsPanelProps {
  projectId?: string;
  userId?: string;
  uploaderType?: 'admin' | 'client';
}

const fileTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    pdf: 'text-negative',
    xlsx: 'text-positive',
    zip: 'text-warning',
    docx: 'text-info',
  };
  return colors[type] ?? 'text-muted-foreground';
};

export default function DocumentsPanel({ projectId, userId, uploaderType = 'client' }: DocumentsPanelProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getDocuments(projectId).then((data) => {
      setDocuments(data || []);
      setLoading(false);
    });

    // Real-time subscription
    const supabase = createClient();
    const channel = supabase
      .channel(`documents_${projectId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'documents', filter: `project_id=eq.${projectId}` }, (payload) => {
        setDocuments((prev) => [payload.new as Document, ...prev]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  const handleUpload = async (file: File) => {
    if (!projectId || !userId) {
      toast.error('Project not loaded yet.');
      return;
    }
    setUploading(true);
    try {
      const result = await uploadDocument(projectId, userId, uploaderType, file);
      if (result) {
        toast.success(`${file.name} uploaded successfully.`);
      } else {
        toast.error('Upload failed. Please try again.');
      }
    } catch {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleUpload(files[0]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) handleUpload(files[0]);
  };

  const handleDownload = async (doc: Document) => {
    const url = await downloadDocument(doc.storage_path);
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Could not generate download link.');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="card">
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <h2 className="text-base font-bold text-foreground">Documents</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{documents.length} files shared on this project</p>
      </div>

      <div
        className={`mx-4 mt-4 border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 cursor-pointer ${
          isDragging ? 'border-primary bg-secondary' : 'border-border hover:border-primary/50 hover:bg-muted/50'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-upload-input')?.click()}
      >
        <input id="file-upload-input" type="file" className="hidden" onChange={handleFileChange} multiple />
        {uploading ? (
          <svg className="w-6 h-6 animate-spin text-primary mx-auto mb-1" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : (
          <svg className="w-6 h-6 text-muted-foreground mx-auto mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
        )}
        <p className="text-xs text-muted-foreground">
          {uploading ? 'Uploading...' : <><span className="text-primary font-semibold">Upload a file</span> or drag &amp; drop</>}
        </p>
      </div>

      <div className="p-4 space-y-1">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <svg className="w-5 h-5 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No documents yet.</p>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors duration-150 group">
              <div className={`w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 ${fileTypeColor(doc.file_type)}`}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{doc.file_name}</p>
                <p className="text-xs text-muted-foreground">{doc.file_size} · {formatDate(doc.created_at)} · {doc.category}</p>
              </div>
              <button
                onClick={() => handleDownload(doc)}
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 btn-ghost p-1.5"
                aria-label={`Download ${doc.file_name}`}
                title={`Download ${doc.file_name}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}